import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  Output,
  EventEmitter,
  ViewChild
} from '@angular/core';
import { Router } from '@angular/router';
import {
  TRANSLOCO_SCOPE,
  TranslocoModule,
  TranslocoService,
} from '@jsverse/transloco';
import { TranslationService } from 'src/app/services/translation.service';
import { LocaleService } from 'src/app/services/locale.service';
import {
  QaiTableComponent,
  TableRow,
  QaiLoadingComponent,
  HeaderConfig,
  SortDirection,
} from '@qai/ui';
import { IPracticeAssignment } from 'src/app/interfaces/reportingV2/ipractice-assignment';
import { IPracticeAssignmentStatsResponse } from 'src/app/interfaces/reportingV2/ipractice-assignment-stats-response';
import { FeatureFlagsService } from 'src/app/services/feature-flags.service';
import { AppConstants } from 'src/app/app-constants';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { AmplitudeTrackingEvents } from 'src/app/utils/enums/amplitude/amplitudeTrackingEvents.enum';

@Component({
  selector: 'app-practice-table',
  templateUrl: './practice-table.component.html',
  styleUrls: ['./practice-table.component.scss'],
  standalone: true,
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: 'reportingV2',
    },
  ],
  imports: [
    TranslocoModule,
    CommonModule,
    QaiTableComponent,
    QaiLoadingComponent,
  ],
})
export class PracticeTableComponent implements OnInit, OnChanges {
  @Input() practiceAssignmentStats: IPracticeAssignmentStatsResponse[] = [];
  @Output() visibleAssignmentsChanged = new EventEmitter<IPracticeAssignmentStatsResponse[]>();

  @ViewChild(QaiTableComponent) tableComponent?: QaiTableComponent;

  isTranslationLoaded: boolean = false;
  isLoading: boolean = false;
  tableData: TableRow[] = [];
  headers: HeaderConfig[] = [];
  initialSortColumn: string = '';
  initialSortDirection: SortDirection = 'desc';
  showAssignmentDetail: boolean = false;
  engagementStatus: string = '';
  private idToStatsMap = new Map<string | number, IPracticeAssignmentStatsResponse>();

  constructor(
    private translocoService: TranslocoService,
    private translationService: TranslationService,
    private localeService: LocaleService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private featureFlagsService: FeatureFlagsService,
    private amplitudeService: AmplitudeService
  ) { }

  async ngOnInit() {
    this.translationService.loadScopedTranslation('reportingV2').then(() => {
      this.isTranslationLoaded = true;
      this.initializeTable();
    });
    this.showAssignmentDetail = await this.featureFlagsService.isFlagEnabled(
      AppConstants.INDIVIDUAL_ASSIGNMENT_VIEW
    );
    console.log(
      `Flag for INDIVIDUAL_ASSIGNMENT_VIEW: ${this.showAssignmentDetail}`
    );
  }

  onPageChanged(event: { page: number; pageSize: number }): void {
    this.emitVisibleAssignments();
  }

  onSortChanged(event: { column: string; direction: 'asc' | 'desc' | null }): void {
    this.emitVisibleAssignments();
  }

  private emitVisibleAssignments(): void {
    if (!this.tableComponent || !this.practiceAssignmentStats || this.practiceAssignmentStats.length === 0) {
      this.visibleAssignmentsChanged.emit([]);
      return;
    }

    setTimeout(() => {
      const paginatedData = this.tableComponent!.paginatedData;
      
      const visibleAssignments = paginatedData
        .map(row => this.idToStatsMap.get(row.id))
        .filter((stat): stat is IPracticeAssignmentStatsResponse => stat !== undefined);
      
      this.visibleAssignmentsChanged.emit(visibleAssignments);
    }, 0);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['practiceAssignmentStats']) {
      setTimeout(() => {
        this.updateTableData();
      }, 0);
    }
  }

  private initializeTable() {
    this.headers = [
      {
        name: this.translocoService.translate(
          'ASSIGNMENT_NAME',
          {},
          'reportingV2'
        ),
        sortable: true,
        columnWidth: '26%',
      },
      {
        name: this.translocoService.translate(
          'START_DATE_LABEL',
          {},
          'reportingV2'
        ),
        sortable: true,
        columnWidth: '13%',
      },
      {
        name: this.translocoService.translate(
          'END_DATE_LABEL',
          {},
          'reportingV2'
        ),
        sortable: true, 
        columnWidth: '13%',
      },
      {
        name: this.translocoService.translate(
          'USERS_ASSIGNED_LABEL',
          {},
          'reportingV2'
        ),
        sortable: true,
        columnWidth: '14%',
        clickable: true,
      },
      {
        name: this.translocoService.translate(
          'IN_PROGRESS_LABEL',
          {},
          'reportingV2'
        ),
        icon: 'info-circle',
        tooltip: this.translocoService.translate(
          'ENGAGED_TOOLTIP',
          {},
          'reportingV2'
        ),
        sortable: true,
        columnWidth: '17%',
        clickable: true,
      },
      {
        name: this.translocoService.translate(
          'PROFICIENT_LABEL',
          {},
          'reportingV2'
        ),
        icon: 'info-circle',
        tooltip: this.translocoService.translate(
          'PROFICIENT_TOOLTIP',
          {},
          'reportingV2'
        ),
        sortable: true,
        columnWidth: '17%',
        clickable: true,
      },
    ];

    this.initialSortDirection = 'desc';

    this.tableData = [];
    this.setInitialSortColumn();
  }

  private setInitialSortColumn(): void {
    // Check if all End date properties in tableData are empty strings
    const endDateColumnName = this.translocoService.translate('END_DATE_LABEL', {}, 'reportingV2');
    const allEndDatesEmpty = this.tableData.every(row => {
      const endDateCell = row[endDateColumnName];
      return endDateCell && typeof endDateCell === 'object' && 'value' in endDateCell 
        ? endDateCell.value === '' || endDateCell.value === null || endDateCell.value === undefined || 
          (typeof endDateCell.value === 'string' && endDateCell.value.trim() === '')
        : true;
    });

    if (allEndDatesEmpty && this.tableData.length > 0) {
      // If all End dates are empty, use START_DATE_LABEL as initial sort column
      this.initialSortColumn = this.translocoService.translate(
        'START_DATE_LABEL',
        {},
        'reportingV2'
      );
    } else {
      // Default to END_DATE_LABEL
      this.initialSortColumn = this.translocoService.translate(
        'END_DATE_LABEL',
        {},
        'reportingV2'
      );
    }
    
    this.initialSortDirection = 'desc';
  }

  private updateTableData(): void {
    if (
      this.practiceAssignmentStats &&
      this.practiceAssignmentStats.length > 0
    ) {
      this.idToStatsMap.clear();

      const convertedAssignments: IPracticeAssignment[] =
        this.practiceAssignmentStats.map((stat) => {
          this.idToStatsMap.set(stat.moduleAssignmentUuid, stat);

          // Calculate total users for percentage calculation
          const totalUsers =
            stat.assignedUsersCount +
            stat.inProgressUsersCount +
            stat.proficientUsersCount;

          const assignedUsersPercentage =
            totalUsers > 0
              ? Math.round((stat.assignedUsersCount / totalUsers) * 100)
              : 0;

          const engagedPercentage =
            totalUsers > 0
              ? Math.round((stat.inProgressUsersCount / totalUsers) * 100)
              : 0;

          const proficientPercentage =
            totalUsers > 0
              ? Math.round((stat.proficientUsersCount / totalUsers) * 100)
              : 0;

          return {
            id: stat.moduleAssignmentUuid,
            name: stat.moduleAssignmentName,
            startDate: stat.startDate,
            endDate: stat.endDate,
            usersAssigned: totalUsers,
            engaged: engagedPercentage,
            proficient: proficientPercentage,
          };
        });

      this.tableData = this.createTableRows(convertedAssignments);

      // Set initial sort column based on data
      this.setInitialSortColumn();

      this.cdr.detectChanges();
      this.emitVisibleAssignments();
    } else {
      this.tableData = [];
      this.setInitialSortColumn();
      this.idToStatsMap.clear();
      this.visibleAssignmentsChanged.emit([]);
      console.log('No practice assignment stats, clearing table data');
      this.cdr.detectChanges();
    }
  }

  private createTableRows(assignments: IPracticeAssignment[]): TableRow[] {
    return assignments.map((assignment) => {
      const row: any = {
        id: assignment.id,
        [this.translocoService.translate('ASSIGNMENT_NAME', {}, 'reportingV2')]:
          { type: 'text', value: assignment.name },
        [this.translocoService.translate(
          'START_DATE_LABEL',
          {},
          'reportingV2'
        )]: {
          type: 'text',
          value: this.localeService.formatDate(assignment.startDate),
        },
        [this.translocoService.translate('END_DATE_LABEL', {}, 'reportingV2')]:
        {
          type: 'text',
          value: this.localeService.formatDate(assignment.endDate),
        },
        [this.translocoService.translate(
          'USERS_ASSIGNED_LABEL',
          {},
          'reportingV2'
        )]: { type: 'text', value: assignment.usersAssigned.toString() },
        [this.translocoService.translate('IN_PROGRESS_LABEL', {}, 'reportingV2')]: {
          type: 'text',
          value: `${assignment.engaged}%`,
        },
        [this.translocoService.translate(
          'PROFICIENT_LABEL',
          {},
          'reportingV2'
        )]: { type: 'text', value: `${assignment.proficient}%` },
      };
      return row as TableRow;
    });
  }

  onTableRowClick(event: { rowId: string | number; rowData: TableRow }) {
    // Track practice assignment click
    const assignmentName = this.extractAssignmentName(event.rowData);
    this.amplitudeService.trackEvent(AmplitudeTrackingEvents.REPORTINGDASHBOARD_CLICK_PRACTICEASSIGNMENT, {
      assignment_name: assignmentName
    });

    if (this.showAssignmentDetail) {
      this.navigateToPracticeDetail(event.rowId);
    }
  }

  navigateToPracticeDetail(practiceId: string | number) {
    const assignmentData = this.idToStatsMap.get(practiceId);
    
    if (assignmentData) {
      this.router.navigate(['/reporting_dashboard/practice', practiceId], {
        queryParams: {
          engagement_status: this.engagementStatus},
        state: { 
          heading: assignmentData.moduleAssignmentName,
          startDate: assignmentData.startDate,
          endDate: assignmentData.endDate,
          callType: AppConstants.TYPE_PRACTICE
        }
      });
    }
  }

  private extractAssignmentName(rowData: TableRow): string {
    const assignmentNameKey = this.translocoService.translate('ASSIGNMENT_NAME', {}, 'reportingV2');
    const cell = rowData[assignmentNameKey];

    if (cell && typeof cell === 'object' && 'value' in cell) {
      const value = cell.value;
      if (typeof value === 'string') {
        return value || 'Unknown';
      } else if (Array.isArray(value)) {
        return value.join(', ') || 'Unknown';
      }
    }

    return 'Unknown';
  }

  onCellClick($event: { rowData: TableRow; columnName: string; event: Event }) {
    this.engagementStatus = $event.columnName;
    let status = $event.columnName;
    // Track user count cell click
    const assignmentName = this.extractAssignmentName($event.rowData);

    this.amplitudeService.trackEvent(AmplitudeTrackingEvents.REPORTINGDASHBOARD_CLICK_USERCOUNTCELL, {
      assignment_name: assignmentName,
      status: status
    });

    if (this.showAssignmentDetail) {
      this.navigateToPracticeDetail($event.rowData.id);
    }
  }
}